import React, { useState, useEffect, useRef } from "react";
import { useQuery } from "react-query";
import axios from "../../config/axiosInstance";
import { USERS } from "../../config/apiConfig";
import DrawerLoading from "../DrawerLoading";
import ForbiddenQueriesPermission from "../common/ForbiddenQueriesPermission";
import { FaCar, FaSearch, FaSearchDollar, FaTools } from "react-icons/fa";
import CustomVehicleQuery from "../queries/CustomVehicleQuery";
import { motion } from "framer-motion";
import { Tooltip } from "react-tooltip";
import { formatToReal } from "../../utils/utils";
import { useApplication } from "../../contexts/ApplicationContext";

const QueriesAutoTable = () => {
  const { user } = useApplication();
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [selectedPrice, setSelectedPrice] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [isListVisible, setIsListVisible] = useState(true);

  const { isLoading, data: userQueryPermissions } = useQuery(
    ["userQueryPermissions"],
    async () => {
      const { data: items } = await axios.get(
        `${USERS.GET_USER_QUERY_PERMISSIONS}`
      );
      return items.data.filter(
        (query) => query.categoryId === 3 && query.status != -1
      );
    }
  );

  const filteredQueries = userQueryPermissions?.filter(
    (query) =>
      query?.name?.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !query.isModular
  );

  const sortedQueries = filteredQueries?.sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  const crlvQueries = sortedQueries?.filter((q) =>
    q.name.toLowerCase().includes("crlv")
  );
  const atpvQueries = sortedQueries?.filter((q) =>
    q.name.toLowerCase().includes("atpv")
  );
  const otherVehicleQueries = sortedQueries?.filter(
    (q) =>
      !q.name.toLowerCase().includes("crlv") &&
      !q.name.toLowerCase().includes("atpv")
  );

  const handleQueryClick = (query) => {
    setSelectedQuery(query);
    setIsListVisible(false);
  };

  const handleBackClick = () => {
    setIsListVisible(true);
    setSelectedQuery(null);
  };

  const QueryCard = ({ query, index }) => {
    const titleRef = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useEffect(() => {
      const el = titleRef.current;
      if (el) {
        requestAnimationFrame(() => {
          setIsTruncated(el.scrollWidth > el.clientWidth);
        });
      }
    }, [query.name]);

    return (
      <>
        {" "}
        {query?.status == 1 ? (
          <motion.div
            key={query.id}
            initial={{ opacity: 0, scale: 0.97, y: 6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.35, delay: index * 0.04 }}
            onClick={() => handleQueryClick(query)}
            className="relative group cursor-pointer rounded-lg border border-gray-200 bg-gradient-to-r from-gray-50 to-white p-4 shadow-sm transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
          >
            {/* Botão flutuante fora do card */}
            <div className="absolute -top-3 right-3 z-20 opacity-0 group-hover:opacity-100 transition-all duration-300 scale-90 group-hover:scale-100">
              <div className="bg-gradient-to-r from-blue-500 to-green-400 text-white text-[11px] font-semibold px-3 py-[2px] rounded-full shadow">
                CONSULTAR AGORA
              </div>
            </div>

            {/* Ícone decorativo */}
            <FaCar className="absolute bottom-2 right-2 text-gray-200 text-4xl z-0 group-hover:text-green-300 transition-colors duration-300" />

            {/* Conteúdo */}
            <div className="relative z-10 flex flex-col gap-1">
              <h3
                className="truncate text-sm font-semibold text-gray-900"
                title={query.name}
              >
                {query.name?.toUpperCase()}
              </h3>
              <span className="text-[11px] text-gray-500">
                {user?.Company?.showPriceQuery && query?.price
                  ? `${formatToReal(query?.price)}  - `
                  : ""}{" "}
                Consulta Veicular
              </span>
            </div>

            {/* Faixa inferior */}
            <div className="absolute bottom-0 left-0 h-[2px] w-full bg-gradient-to-r from-blue-500 to-green-400 transition-all duration-300 opacity-40 group-hover:opacity-80 rounded-b-lg" />
          </motion.div>
        ) : (
          <motion.div
            key={query.id}
            initial={{ opacity: 0, scale: 0.97, y: 6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.35, delay: index * 0.04 }}
            className="bg-gradient-to-r from-gray-100 to-gray-200 border border-gray-300 rounded-lg p-4 shadow-sm cursor-not-allowed relative group overflow-hidden opacity-50 pointer-events-none"
          >
            {/* Conteúdo principal */}
            <div className="flex flex-col gap-1 relative z-10">
              <div className="flex items-center gap-2">
                <FaTools className="text-gray-500 text-sm" />
                <h3
                  className="text-sm font-semibold text-gray-600 truncate"
                  title={query.name}
                >
                  {query.name?.toUpperCase()}
                </h3>
              </div>
              <span className="text-[11px] text-gray-500">
                Consulta em manutenção
              </span>
            </div>

            {/* Ícone decorativo no canto */}
            <FaTools className="absolute bottom-2 right-2 text-gray-300 text-4xl z-0" />

            {/* Faixa inferior desativada */}
            <div className="absolute bottom-0 left-0 h-[2px] w-full bg-gray-400 rounded-b-lg z-0" />

            {/* Hover: painel de manutenção */}
            <div className="absolute bottom-0 left-0 w-full overflow-hidden transition-all duration-300 ease-in-out h-0 group-hover:h-8 z-10">
              <div className="flex items-center justify-center bg-gray-400 text-white text-[11px] font-semibold h-8 opacity-100 transition-opacity duration-300 rounded-b-lg shadow-inner">
                EM MANUTENÇÃO
              </div>
            </div>
          </motion.div>
        )}
      </>
    );
  };

  if (isLoading) return <DrawerLoading />;
  if (!userQueryPermissions?.length) return <ForbiddenQueriesPermission />;

  return (
    <div className="w-full px-4 md:px-4 pt-4 min-h-screen">
      {isListVisible ? (
        <>
          <div className="mb-3 relative">
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar consultas veiculares..."
                className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 shadow-sm focus:ring-2 focus:ring-blue-600 focus:outline-none text-sm sm:text-base placeholder-gray-400 transition-all"
              />
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-700 pointer-events-none">
                <FaSearch size={18} />
              </div>
            </div>
          </div>

          {otherVehicleQueries?.length > 0 && (
            <>
              <h2 className="text-xl font-bold text-gray-800 mb-3 flex items-center gap-2">
                🚘 <span>Consultas Gerais</span>
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-3 2xl:grid-cols-4 gap-4 mb-6 bg-gray-50 rounded-md p-4 shadow-sm border border-gray-200">
                {otherVehicleQueries.map((query, index) => (
                  <QueryCard query={query} index={index} />
                ))}
              </div>
            </>
          )}

          {crlvQueries?.length > 0 && (
            <>
              <h2 className="text-xl font-bold text-green-700 mb-3 flex items-center gap-2">
                📄 <span>CRLV</span>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6 bg-gray-50 rounded-md p-4 shadow-sm border border-gray-200">
                {crlvQueries.map((query, index) => (
                  <QueryCard query={query} index={index} />
                ))}
              </div>
            </>
          )}

          {atpvQueries?.length > 0 && (
            <>
              <h2 className="text-xl font-bold text-yellow-700 mb-3 flex items-center gap-2">
                📝 <span>ATPV</span>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6 bg-gray-50 rounded-md p-4 shadow-sm border border-gray-200">
                {atpvQueries.map((query, index) => (
                  <QueryCard query={query} index={index} />
                ))}
              </div>
            </>
          )}
        </>
      ) : (
        <div>
          <button
            onClick={handleBackClick}
            className="flex items-center text-sm text-green-700 hover:text-green-900 font-medium mb-4 transition-all"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Voltar para a listagem
          </button>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            transition={{ duration: 0.4 }}
          >
            <CustomVehicleQuery query={selectedQuery} selectedPrice={10} />
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default QueriesAutoTable;
